import type { BehaviorNode, OperatorNode } from "../blueprint/types.js";
import { resolvePath } from "../layout/resolution.js";
import type { CompiledValue, RuntimeContext } from "./types.js";
import {
  BinaryOperatorOptions,
  UnaryOperatorOptions,
  coerceToBoolean,
  evaluateListFind,
  evaluateListFindByPath,
  evaluateListFindByPathIndex,
  evaluateListFindIndex,
  normalizeColorString,
  toListValue,
} from "./helpers.js";
import {
  evaluateBinaryOperator,
  evaluateTernaryOperator,
  evaluateUnaryOperator,
} from "./evaluate-operators.js";
import {
  normalizeAnimationDirectionValue,
  normalizeAnimationEasingValue,
  normalizeAnimationFillModeValue,
  normalizeFontFamilyValue,
  normalizeFontWeightValue,
  normalizeTextAlignValue,
  normalizeTextTransformValue,
  normalizeWhiteSpaceValue,
} from "./normalize.js";

export { coerceToBoolean } from "./helpers.js";
export { resolvePath } from "../layout/resolution.js";

export function stableValueKey(
  value: unknown,
  snapshotCache?: WeakMap<object, string>,
): string {
  if (value == null) return String(value);

  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }

  if (snapshotCache && typeof value === "object") {
    const cached = snapshotCache.get(value);
    if (cached !== undefined) return cached;
    const serialized = JSON.stringify(value);
    snapshotCache.set(value, serialized);
    return serialized;
  }
  return JSON.stringify(value);
}

export function uniqueDependencies(dependencies: string[]): string[] {
  return Array.from(new Set(dependencies));
}

export function mergeCompiledDependencies(
  ...values: CompiledValue[]
): string[] {
  return uniqueDependencies(values.flatMap((value) => value.dependencies));
}

export function getPortValueType(node: BehaviorNode, portId: string): string {
  return node.ports.find((port) => port.id === portId)?.valueType ?? "unknown";
}

export function buildUnaryOperatorOptions(
  node: OperatorNode,
): UnaryOperatorOptions {
  return {
    datePartTextFormat: node.datePartTextFormat,
    input1ValueType: getPortValueType(node, "input1"),
  };
}

export function buildBinaryOperatorOptions(
  node: OperatorNode,
  outputKind?: "index" | "item",
): BinaryOperatorOptions {
  return {
    objectFieldPath: node.objectFieldPath,
    ...(outputKind ? { outputKind } : {}),
  };
}

export {
  evaluateUnaryOperator,
  evaluateBinaryOperator,
  evaluateTernaryOperator,
};

/**
 * Mirrors {@link OperatorBehaviorNode} typed equality for boolean/list/color/animation/font fields.
 */
export function evaluateTypedEquals(
  a: unknown,
  b: unknown,
  input1ValueType: string,
): boolean {
  if (input1ValueType === "boolean") {
    return coerceToBoolean(a) === coerceToBoolean(b);
  }
  if (input1ValueType === "list") {
    return JSON.stringify(toListValue(a)) === JSON.stringify(toListValue(b));
  }
  if (input1ValueType === "color") {
    return normalizeColorString(a) === normalizeColorString(b);
  }
  if (input1ValueType === "animationDirection") {
    return (
      normalizeAnimationDirectionValue(a) ===
      normalizeAnimationDirectionValue(b)
    );
  }
  if (input1ValueType === "animationFill") {
    return (
      normalizeAnimationFillModeValue(a) === normalizeAnimationFillModeValue(b)
    );
  }
  if (input1ValueType === "animationEasing") {
    return (
      normalizeAnimationEasingValue(a) === normalizeAnimationEasingValue(b)
    );
  }
  if (input1ValueType === "fontFamily") {
    return normalizeFontFamilyValue(a) === normalizeFontFamilyValue(b);
  }
  if (input1ValueType === "fontWeight") {
    return normalizeFontWeightValue(a) === normalizeFontWeightValue(b);
  }
  if (input1ValueType === "textAlign") {
    return normalizeTextAlignValue(a) === normalizeTextAlignValue(b);
  }
  if (input1ValueType === "textTransform") {
    return normalizeTextTransformValue(a) === normalizeTextTransformValue(b);
  }
  if (input1ValueType === "whiteSpace") {
    return normalizeWhiteSpaceValue(a) === normalizeWhiteSpaceValue(b);
  }
  return a === b || Number(a) === Number(b);
}

export function evaluateTypedNotEquals(
  a: unknown,
  b: unknown,
  input1ValueType: string,
): boolean {
  return !evaluateTypedEquals(a, b, input1ValueType);
}

export function evaluateUnaryObjectGet(
  value: unknown,
  objectFieldPath?: string,
): unknown {
  if (value == null || typeof value !== "object") {
    return undefined;
  }
  if (!objectFieldPath) {
    return undefined;
  }
  return resolvePath(value, objectFieldPath);
}

export function readCurrentItem(
  ctx: RuntimeContext,
  objectFieldPath?: string,
  list?: unknown,
): unknown {
  const scope =
    list === undefined
      ? undefined
      : ctx.__iterationScopes
          ?.slice()
          .reverse()
          .find((scope) => scope.list === list);
  const item = scope ? scope.item : ctx.iterationItem;
  if (item == null) {
    return undefined;
  }
  if (objectFieldPath) {
    return resolvePath(item, objectFieldPath);
  }
  return item;
}

export function readComparatorItem(
  ctx: RuntimeContext,
  which: "A" | "B",
  objectFieldPath?: string,
): unknown {
  const item = which === "A" ? ctx.iterationItemA : ctx.iterationItemB;
  if (item == null) {
    return undefined;
  }
  if (objectFieldPath) {
    return resolvePath(item, objectFieldPath);
  }
  return item;
}

/** Register only actual list-iteration scopes, never function/variable frames. */
export function withIterationExpressionScope(
  parent: RuntimeContext,
  scoped: RuntimeContext,
): RuntimeContext {
  const cache = parent.__expressionCache;
  if (cache) {
    cache.iterationParentByContext ??= new WeakMap();
    cache.iterationParentByContext.set(scoped, parent);
  }
  return scoped;
}

export function evaluateFindByCondition(
  list: unknown,
  evaluatePredicate: (ctx: RuntimeContext) => unknown,
  wantsIndex: boolean,
  baseCtx: RuntimeContext,
): unknown {
  const arr = toListValue(list);
  for (let index = 0; index < arr.length; index += 1) {
    const scopedCtx = withIterationExpressionScope(baseCtx, {
      ...baseCtx,
      iterationItem: arr[index],
      iterationIndex: index,
      __iterationScopes: [
        ...(baseCtx.__iterationScopes ?? []),
        { list, item: arr[index] },
      ],
    });
    if (coerceToBoolean(evaluatePredicate(scopedCtx))) {
      return wantsIndex ? index : arr[index];
    }
  }
  return wantsIndex ? -1 : undefined;
}

export function evaluateFilter(
  list: unknown,
  evaluatePredicate: (ctx: RuntimeContext) => unknown,
  baseCtx: RuntimeContext,
): unknown[] {
  const arr = toListValue(list);
  const result: unknown[] = [];
  for (let index = 0; index < arr.length; index += 1) {
    const scopedCtx = withIterationExpressionScope(baseCtx, {
      ...baseCtx,
      iterationItem: arr[index],
      iterationIndex: index,
      __iterationScopes: [
        ...(baseCtx.__iterationScopes ?? []),
        { list, item: arr[index] },
      ],
    });
    if (coerceToBoolean(evaluatePredicate(scopedCtx))) {
      result.push(arr[index]);
    }
  }
  return result;
}

export function evaluateSort(
  list: unknown,
  evaluateComparator: (ctx: RuntimeContext) => unknown,
  baseCtx: RuntimeContext,
): unknown[] {
  const arr = toListValue(list);
  const indices = arr.map((_, index) => index);
  indices.sort((aIndex, bIndex) => {
    const scopedCtx: RuntimeContext = {
      ...baseCtx,
      iterationItemA: arr[aIndex],
      iterationItemB: arr[bIndex],
    };
    const result = evaluateComparator(scopedCtx);
    const numeric = Number(result);
    if (!Number.isFinite(numeric) || numeric === 0) {
      return aIndex - bIndex;
    }
    return numeric;
  });
  return indices.map((index) => arr[index]);
}

export function evaluateListGetAtIndex(
  list: unknown,
  indexRaw: unknown,
): unknown {
  const source = toListValue(list);
  const index = Number(indexRaw);
  if (!Number.isInteger(index) || index < 0 || index >= source.length) {
    return undefined;
  }
  return source[index];
}

export function evaluateListFindOutput(
  list: unknown,
  needle: unknown,
  objectFieldPath: string | undefined,
  wantsIndex: boolean,
): unknown {
  return wantsIndex
    ? evaluateListFindIndex(list, needle, objectFieldPath)
    : evaluateListFind(list, needle, objectFieldPath);
}

export function evaluateListFindByPathOutput(
  list: unknown,
  pathRaw: unknown,
  expected: unknown,
  wantsIndex: boolean,
): unknown {
  return wantsIndex
    ? evaluateListFindByPathIndex(list, pathRaw, expected)
    : evaluateListFindByPath(list, pathRaw, expected);
}
